import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type TouchEvent as ReactTouchEvent } from 'react'

interface ImageCropModalProps {
  file: File
  aspectRatio: number
  outputWidth: number
  outputHeight: number
  onCancel: () => void
  onConfirm: (croppedFile: File) => void
}

const FRAME_WIDTH = 480

export default function ImageCropModal({ file, aspectRatio, outputWidth, outputHeight, onCancel, onConfirm }: ImageCropModalProps) {
  const frameHeight = FRAME_WIDTH / aspectRatio
  const imgRef = useRef<HTMLImageElement | null>(null)
  const [imgUrl, setImgUrl] = useState<string>('')
  const [natural, setNatural] = useState({ w: 0, h: 0 })
  const [coverScale, setCoverScale] = useState(1)
  const [zoom, setZoom] = useState(1)
  const [offset, setOffset] = useState({ x: 0, y: 0 })
  const dragState = useRef<{ startX: number; startY: number; offX: number; offY: number } | null>(null)

  useEffect(() => {
    const url = URL.createObjectURL(file)
    setImgUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [file])

  const handleImgLoad = () => {
    const img = imgRef.current
    if (!img) return
    const w = img.naturalWidth
    const h = img.naturalHeight
    const cover = Math.max(FRAME_WIDTH / w, frameHeight / h)
    setNatural({ w, h })
    setCoverScale(cover)
    setZoom(1)
    setOffset({ x: (FRAME_WIDTH - w * cover) / 2, y: (frameHeight - h * cover) / 2 })
  }

  const clamp = (ox: number, oy: number, scale: number) => {
    const w = natural.w * scale
    const h = natural.h * scale
    const minX = Math.min(0, FRAME_WIDTH - w)
    const minY = Math.min(0, frameHeight - h)
    return {
      x: Math.min(0, Math.max(minX, ox)),
      y: Math.min(0, Math.max(minY, oy))
    }
  }

  const onZoomChange = (newZoom: number) => {
    const scale = coverScale * newZoom
    const clamped = clamp(offset.x, offset.y, scale)
    setZoom(newZoom)
    setOffset(clamped)
  }

  const startDrag = (clientX: number, clientY: number) => {
    dragState.current = { startX: clientX, startY: clientY, offX: offset.x, offY: offset.y }
  }

  const moveDrag = (clientX: number, clientY: number) => {
    if (!dragState.current) return
    const scale = coverScale * zoom
    const dx = clientX - dragState.current.startX
    const dy = clientY - dragState.current.startY
    setOffset(clamp(dragState.current.offX + dx, dragState.current.offY + dy, scale))
  }

  const endDrag = () => { dragState.current = null }

  const onMouseDown = (e: ReactMouseEvent) => { e.preventDefault(); startDrag(e.clientX, e.clientY) }
  const onMouseMove = (e: ReactMouseEvent) => { if (dragState.current) moveDrag(e.clientX, e.clientY) }
  const onTouchStart = (e: ReactTouchEvent) => { const t = e.touches[0]; startDrag(t.clientX, t.clientY) }
  const onTouchMove = (e: ReactTouchEvent) => { const t = e.touches[0]; moveDrag(t.clientX, t.clientY) }

  const handleConfirm = () => {
    const scale = coverScale * zoom
    const srcX = -offset.x / scale
    const srcY = -offset.y / scale
    const srcW = FRAME_WIDTH / scale
    const srcH = frameHeight / scale

    const canvas = document.createElement('canvas')
    canvas.width = outputWidth
    canvas.height = outputHeight
    const ctx = canvas.getContext('2d')
    if (!ctx || !imgRef.current) return
    ctx.drawImage(imgRef.current, srcX, srcY, srcW, srcH, 0, 0, outputWidth, outputHeight)
    canvas.toBlob((blob) => {
      if (!blob) return
      const croppedFile = new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' })
      onConfirm(croppedFile)
    }, 'image/jpeg', 0.92)
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
    }}>
      <div style={{ background: '#fff', borderRadius: '12px', padding: '20px', width: `${FRAME_WIDTH + 64}px`, maxWidth: '92vw', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: '15px', color: '#9a3412' }}>🖼️ ছবি ক্রপ করুন</h3>
        <p style={{ margin: '0 0 12px', fontSize: '11px', color: '#78716c' }}>
          মাউস/টাচ দিয়ে ছবি টেনে সঠিক অবস্থানে আনুন এবং জুম স্লাইডার দিয়ে সাইজ ঠিক করুন। ফ্রেমের বাইরের অংশ কাটা যাবে।
        </p>
        <div
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={endDrag}
          style={{
            width: `${FRAME_WIDTH}px`, height: `${frameHeight}px`,
            overflow: 'hidden', position: 'relative', margin: '0 auto',
            border: '2px solid #ea580c', borderRadius: '8px',
            background: '#f1f5f9', cursor: 'grab', touchAction: 'none'
          }}
        >
          {imgUrl && (
            <img
              ref={imgRef}
              src={imgUrl}
              onLoad={handleImgLoad}
              draggable={false}
              alt="crop-preview"
              style={{
                position: 'absolute',
                left: `${offset.x}px`,
                top: `${offset.y}px`,
                width: `${natural.w * coverScale * zoom}px`,
                height: `${natural.h * coverScale * zoom}px`,
                maxWidth: 'none',
                userSelect: 'none',
                pointerEvents: 'none'
              }}
            />
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '14px' }}>
          <span style={{ fontSize: '12px', color: '#64748b' }}>🔍</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => onZoomChange(parseFloat(e.target.value))}
            style={{ flex: 1 }}
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px' }}>
          <button type="button" onClick={onCancel} style={{
            padding: '8px 16px', borderRadius: '7px', border: '1.5px solid #e2e8f0',
            background: '#fff', color: '#475569', fontSize: '13px', cursor: 'pointer'
          }}>বাতিল</button>
          <button type="button" onClick={handleConfirm} style={{
            padding: '8px 18px', borderRadius: '7px', border: 'none',
            background: '#ea580c', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer'
          }}>✅ ক্রপ করে আপলোড করুন</button>
        </div>
      </div>
    </div>
  )
}
